import { cn } from "@/lib/utils";
import { useDashboardAccounts } from "@/contexts/DashboardAccountContext";
import { PlatformIcon } from "./PlatformBadge";
import { useMemo } from "react";

interface PlatformFilterToggleProps {
  className?: string;
}

export function PlatformFilterToggle({ className }: PlatformFilterToggleProps) {
  const { allAccounts, selectedAccountIds, selectAll, togglePlatform, isAllSelected, activePlatforms } = useDashboardAccounts();

  const platforms = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; color: string; count: number }>();
    allAccounts.forEach(a => {
      if (a.platform) {
        const existing = map.get(a.platform.slug);
        if (existing) {
          existing.count++;
        } else {
          map.set(a.platform.slug, { slug: a.platform.slug, name: a.platform.name, color: a.platform.color, count: 1 });
        }
      }
    });
    return Array.from(map.values());
  }, [allAccounts]);

  if (platforms.length <= 1) return null;

  return (
    <div className={cn("flex items-center gap-1 bg-gray-100 rounded-lg p-1", className)} data-testid="platform-filter-toggle">
      <button
        onClick={selectAll}
        className={cn(
          "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
          isAllSelected ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        )}
        data-testid="platform-filter-all"
      >
        All
      </button>
      {platforms.map(p => {
        const isActive = activePlatforms.includes(p.slug);
        return (
          <button
            key={p.slug}
            onClick={() => togglePlatform(p.slug)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
              isActive && !isAllSelected ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            )}
            data-testid={`platform-filter-${p.slug}`}
          >
            <PlatformIcon platform={p.slug} size="sm" />
            {p.name}
          </button>
        );
      })}
    </div>
  );
}
